/**
 * RequisitionsGuide — User guide for Requisitions module.
 */
import React from 'react';
import GuideViewer, { GuideSection, GuideSubSection, GuideCallout, GuideFieldTable, GuideTOC } from './GuideViewer';
import AppShell from '../../components/layout/AppShell';

function GuideContent() {
  const toc = [
    { id: 'overview', label: 'What is a Requisition?' },
    { id: 'dashboard', label: 'Requisitions Dashboard' },
    { id: 'listing', label: 'Requisitions Listing' },
    { id: 'creating', label: 'Creating a Requisition' },
    { id: 'line-items', label: 'Managing Line Items' },
    { id: 'header', label: 'Header Details' },
    { id: 'approval', label: 'Approval Workflow' },
    { id: 'conversion', label: 'Converting to Purchase Orders' },
    { id: 'statuses', label: 'Requisition Statuses' },
    { id: 'features', label: 'Advanced Features' },
    { id: 'reports', label: 'Requisition Reports' },
  ];

  return (
    <GuideViewer title="Requisitions Module" subtitle="Purchase Requisitions User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What is a Requisition?">
        <p className="text-sm text-slate-600 mb-3">
          A Purchase Requisition (PR) is an internal request to purchase goods or services. It is the starting point of the procure-to-pay cycle. Requisitions must be approved before they can be converted into formal Purchase Orders sent to suppliers.
        </p>
        <p className="text-sm text-slate-600 mb-3">
          The requisition process ensures that all purchases are properly authorised, budgeted, and documented before committing the organisation to spend.
        </p>
      </GuideSection>

      <GuideSection id="dashboard" title="2. Requisitions Dashboard">
        <p className="text-sm text-slate-600 mb-3">
          The dashboard provides KPIs and visualisations:
        </p>
        <GuideFieldTable fields={[
          { name: 'Total PRs', desc: 'Total number of requisitions in the system.' },
          { name: 'Total Value', desc: 'Combined net value of all requisitions.' },
          { name: 'MTD Submissions', desc: 'Number of requisitions submitted this month.' },
          { name: 'Pending Approval', desc: 'Requisitions awaiting approval.' },
          { name: 'Approved (PO)', desc: 'Requisitions that have been approved and converted to POs.' },
          { name: 'Rejected', desc: 'Requisitions that were rejected during approval.' },
          { name: 'Approval Pipeline', desc: 'Shows aging buckets (0-3 days, 4-7 days, 7+ days) for pending approvals.' },
          { name: 'Charts', desc: 'Status breakdown, submission trends, top requestors, and spend by department.' },
        ]} />
      </GuideSection>

      <GuideSection id="listing" title="3. Requisitions Listing">
        <p className="text-sm text-slate-600 mb-3">
          The listing shows all requisitions with sortable, filterable columns:
        </p>
        <GuideFieldTable fields={[
          { name: 'Request Number', desc: 'System-generated unique reference (e.g., "REQ-DASH-001").' },
          { name: 'Reference', desc: 'Optional user-provided reference number.' },
          { name: 'Status', desc: 'Current status (Draft, Unapproved, Pending, Approved, Order, etc.).' },
          { name: 'Entry Date', desc: 'Date the requisition was created.' },
          { name: 'Net Value', desc: 'Total net value of all line items.' },
          { name: 'Description', desc: 'Brief description of the requisition purpose.' },
          { name: 'Originator', desc: 'The user who created the requisition.' },
        ]} />
        <p className="text-sm text-slate-600 mb-2">
          Click any row to open the requisition detail view. Use the status filter dropdown to focus on specific statuses (e.g., "PENDING" to see all awaiting approval).
        </p>
      </GuideSection>

      <GuideSection id="creating" title="4. Creating a Requisition">
        <p className="text-sm text-slate-600 mb-3">
          Click <strong>+ New Requisition</strong>. A new requisition is created in <strong>DRAFT</strong> status. You can now add header details and line items.
        </p>
        <GuideSubSection title="Header Fields">
          <GuideFieldTable fields={[
            { name: 'Description', desc: 'A title describing the overall purchase need.' },
            { name: 'Document Type', desc: 'The type of purchase (Standard, Urgent, Capital, etc.).' },
            { name: 'Delivery Point', desc: 'Where the goods should be delivered.' },
            { name: 'Invoice Point', desc: 'Where the invoice should be sent.' },
            { name: 'Required By Date', desc: 'The date by which the goods/services are needed.' },
            { name: 'Currency', desc: 'Transaction currency for this requisition.' },
            { name: 'Shipping Method', desc: 'Preferred shipping method (Standard, Express, Freight, etc.).' },
            { name: 'Incoterms', desc: 'International commercial terms (EXW, FOB, CIF, etc.).' },
            { name: 'Notes', desc: 'Free-text notes for the approver or supplier.' },
          ]} />
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="line-items" title="5. Managing Line Items">
        <p className="text-sm text-slate-600 mb-3">
          Line items are the individual products or services being requested. Each line item has:
        </p>
        <GuideFieldTable fields={[
          { name: 'Item Number', desc: 'Sequential line number (auto-assigned, can be reordered via drag-and-drop).' },
          { name: 'Item Description', desc: 'What is being purchased.' },
          { name: 'Supplier', desc: 'The preferred supplier for this line (optional at requisition stage).' },
          { name: 'Due Date', desc: 'When this specific line item is needed.' },
          { name: 'Quantity', desc: 'Number of units required.' },
          { name: 'Unit', desc: 'Unit of measure (EACH, BOX, KG, etc.).' },
          { name: 'Pack Size', desc: 'Number of units per pack.' },
          { name: 'Discount %', desc: 'Any applicable discount percentage.' },
          { name: 'Unit Price', desc: 'Price per unit.' },
          { name: 'Net Price', desc: 'Calculated: Quantity x Unit Price x Pack Size x (1 - Discount%).' },
          { name: 'Account Codes', desc: 'Expenditure codes for budget allocation (one field per segment).' },
        ]} />
        <GuideSubSection title="Adding Line Items">
          <p className="text-sm text-slate-600 mb-2">You can add line items in several ways:</p>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
            <li><strong>Manual Entry</strong> — Click "+ Add Item" and fill in the fields</li>
            <li><strong>From Catalogue</strong> — Browse your item master and select items to add</li>
            <li><strong>Punchout</strong> — If configured, browse external supplier catalogues</li>
            <li><strong>Excel Import</strong> — Upload a spreadsheet of line items</li>
            <li><strong>Copy from Template</strong> — Use a saved requisition template</li>
          </ul>
        </GuideSubSection>
        <GuideSubSection title="Line Item Actions">
          <GuideFieldTable fields={[
            { name: 'Edit', desc: 'Click a line item row to expand and edit its details inline.' },
            { name: 'Delete', desc: 'Remove a line item using the trash icon.' },
            { name: 'Reorder', desc: 'Drag line items to change their sequence.' },
            { name: 'Split', desc: 'Split a line item across multiple account codes or suppliers.' },
            { name: 'Source', desc: 'Open the Item Sourcing panel to compare supplier prices for this item.' },
          ]} />
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="header" title="6. Header Details">
        <p className="text-sm text-slate-600 mb-3">
          The requisition detail view has multiple tabs beyond line items:
        </p>
        <GuideFieldTable fields={[
          { name: 'Line Items', desc: 'The main tab showing all items being requested.' },
          { name: 'Attachments', desc: 'Upload supporting documents (quotes, specifications, images).' },
          { name: 'Notes', desc: 'Internal notes visible to approvers and procurement team.' },
          { name: 'Approval History', desc: 'Shows the approval trail: who approved/rejected, when, and their comments.' },
          { name: 'Activity', desc: 'An audit log of all actions taken on this requisition.' },
        ]} />
      </GuideSection>

      <GuideSection id="approval" title="7. Approval Workflow">
        <p className="text-sm text-slate-600 mb-3">
          When a requisition is complete, click <strong>Submit for Approval</strong>. The requisition moves from DRAFT to UNAPPROVED/PENDING status and enters the approval workflow:
        </p>
        <ol className="list-decimal list-inside text-sm text-slate-600 space-y-2 mb-3">
          <li><strong>DRAFT</strong> — Being composed by the originator. Can be freely edited.</li>
          <li><strong>UNAPPROVED</strong> — Submitted but not yet picked up by an approver.</li>
          <li><strong>PENDING</strong> — Under review by an approver.</li>
          <li><strong>APPROVED</strong> — All required approvals received. Ready for conversion to PO.</li>
          <li><strong>REJECTED</strong> — Rejected by an approver. Can be revised and resubmitted.</li>
        </ol>
        <GuideCallout type="info" title="Approval Rules">
          <p>Your administrator configures approval rules based on requisition value, document type, cost centre, or department. Multi-level approvals may be required for high-value requisitions.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="conversion" title="8. Converting to Purchase Orders">
        <p className="text-sm text-slate-600 mb-3">
          Once approved, a requisition can be converted to one or more Purchase Orders. The conversion process:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Groups line items by supplier (each supplier gets a separate PO)</li>
          <li>Carries forward all details (items, quantities, prices, delivery points)</li>
          <li>Links the PO back to the originating requisition for traceability</li>
        </ul>
        <p className="text-sm text-slate-600 mt-2">
          After conversion, the requisition status changes to <strong>ORDER</strong> and shows the linked PO number(s).
        </p>
      </GuideSection>

      <GuideSection id="statuses" title="9. Requisition Statuses">
        <GuideFieldTable fields={[
          { name: 'DRAFT', desc: 'Being composed. Fully editable.' },
          { name: 'UNAPPROVED', desc: 'Submitted for approval but not yet reviewed.' },
          { name: 'PENDING', desc: 'Currently being reviewed by an approver.' },
          { name: 'APPROVED', desc: 'Approved and ready for PO conversion.' },
          { name: 'ORDER', desc: 'Converted to a purchase order.' },
          { name: 'RFQ', desc: 'Sent to an RFx sourcing event for competitive bidding.' },
          { name: 'DORMANT', desc: 'Parked/on hold. Can be reactivated.' },
          { name: 'TEMPLATE', desc: 'Saved as a template for future reuse.' },
          { name: 'REJECTED', desc: 'Rejected by an approver.' },
          { name: 'CANCELLED', desc: 'Cancelled and no longer active.' },
        ]} />
      </GuideSection>

      <GuideSection id="features" title="10. Advanced Features">
        <GuideSubSection title="Budget Chart">
          <p className="text-sm text-slate-600 mb-2">
            If account codes have budgets assigned, a budget chart shows remaining budget vs. spend for the selected account codes. This helps requestors stay within budget before submitting.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Item Sourcing">
          <p className="text-sm text-slate-600 mb-2">
            The Item Sourcing panel compares prices from different suppliers for a given item, helping you select the best-value supplier.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Update All">
          <p className="text-sm text-slate-600 mb-2">
            The "Update All" feature allows you to apply the same change (supplier, delivery point, account code, etc.) across all line items at once, saving time on large requisitions.
          </p>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="reports" title="11. Requisition Reports">
        <p className="text-sm text-slate-600 mb-3">
          The Reports tab provides detailed requisition listings with filtering by status, date range, originator, value, and account codes. Reports can be exported to CSV, PDF, or emailed.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export default function RequisitionsGuidePage() {
  return <AppShell><GuideContent /></AppShell>;
}
export function RequisitionsGuideEmbed() {
  return <GuideContent />;
}
